import { useState, useEffect, useCallback, useRef } from 'react';
import { EngineService, SpriteLevel } from '@/services/engine';
import {
    SpriteCue,
    SpriteInfo,
    parseVtt,
    findCueForTime,
    extractSpriteIndex,
    getUniqueSpriteFiles,
} from '@/utils/sprite-vtt';

interface UseSpritePreviewOptions {
    sourceId: string | undefined;
    level?: SpriteLevel;
}

interface UseSpritePreviewResult {
    isLoading: boolean;
    isReady: boolean;
    error: string | null;
    getSprite: (time: number) => SpriteInfo | null;
}

const vttCache = new Map<string, SpriteCue[]>();

function buildCacheKey(sourceId: string, level: SpriteLevel): string {
    return `${sourceId}-${level}`;
}

function buildSpriteUrlMap(
    cues: SpriteCue[],
    sourceId: string,
    level: SpriteLevel,
): Map<string, string> {
    const urlMap = new Map<string, string>();

    getUniqueSpriteFiles(cues).forEach((spriteFile) => {
        const index = extractSpriteIndex(spriteFile);
        const url = EngineService.getSourceSpriteSheetUrl(
            sourceId,
            level,
            index,
        );
        urlMap.set(spriteFile, url);
    });

    return urlMap;
}

export function useSpritePreview({
    sourceId,
    level = SpriteLevel.SD,
}: UseSpritePreviewOptions): UseSpritePreviewResult {
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cuesRef = useRef<SpriteCue[]>([]);
    const spriteUrlsRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        if (!sourceId) {
            setIsReady(false);
            return;
        }

        const initializeFromCues = (cues: SpriteCue[]) => {
            cuesRef.current = cues;
            spriteUrlsRef.current = buildSpriteUrlMap(cues, sourceId, level);
            setIsReady(true);
        };

        const cacheKey = buildCacheKey(sourceId, level);
        const cached = vttCache.get(cacheKey);

        if (cached) {
            initializeFromCues(cached);
            return;
        }

        const fetchVtt = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const vttContent = await EngineService.fetchSourceSpriteVtt(
                    sourceId,
                    level,
                );
                const cues = parseVtt(vttContent);

                vttCache.set(cacheKey, cues);
                initializeFromCues(cues);
            } catch (err) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Failed to load sprites';
                setError(message);
                setIsReady(false);
            } finally {
                setIsLoading(false);
            }
        };

        fetchVtt();
    }, [sourceId, level]);

    const getSprite = useCallback((time: number): SpriteInfo | null => {
        const cue = findCueForTime(cuesRef.current, time);
        if (!cue) return null;

        const url = spriteUrlsRef.current.get(cue.spriteFile);
        if (!url) return null;

        return {
            url,
            x: cue.x,
            y: cue.y,
            width: cue.width,
            height: cue.height,
        };
    }, []);

    return {
        isLoading,
        isReady,
        error,
        getSprite,
    };
}
